"use client";

import { useCallback, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useAuth } from "@/lib/auth-context";

export interface FileUploadState {
  file: File;
  progress: number;
  status: "idle" | "uploading" | "success" | "error";
  error?: string;
  url?: string;
  storageId?: string;
}

export function useUpload() {
  const { token } = useAuth();
  const generateUploadUrl = useMutation(api.media.generateUploadUrl);
  const saveMedia = useMutation(api.media.saveMedia);
  const [uploads, setUploads] = useState<FileUploadState[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const patchUpload = useCallback((file: File, patch: Partial<FileUploadState>) => {
    setUploads((current) => current.map((item) =>
      item.file === file ? { ...item, ...patch } : item
    ));
  }, []);

  const performUpload = useCallback(async (file: File, folder = "General") => {
    patchUpload(file, { progress: 10, status: "uploading", error: undefined });

    try {
      const postUrl = await generateUploadUrl({ token: token ?? undefined });
      patchUpload(file, { progress: 40 });

      const result = await fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });

      if (!result.ok) throw new Error(`Upload failed: ${result.statusText}`);

      const { storageId } = await result.json();
      patchUpload(file, { progress: 80, storageId });

      const mediaId = await saveMedia({
        filename: file.name,
        storageId,
        type: file.type.startsWith("image/")
          ? "image"
          : file.type.startsWith("video/")
            ? "video"
            : "file",
        folder,
        token: token ?? undefined,
      });

      const completed = { storageId, url: `/api/storage/${storageId}`, mediaId };
      patchUpload(file, { progress: 100, status: "success", ...completed });
      return completed;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed";
      patchUpload(file, { progress: 0, status: "error", error: message });
      throw error;
    }
  }, [generateUploadUrl, patchUpload, saveMedia, token]);

  const uploadFile = useCallback(async (file: File, folder = "General") => {
    setUploads([{ file, progress: 0, status: "idle" }]);
    setIsUploading(true);
    try {
      return await performUpload(file, folder);
    } finally {
      setIsUploading(false);
    }
  }, [performUpload]);

  const uploadFiles = useCallback(async (files: File[], folder = "General") => {
    setUploads(files.map((file) => ({ file, progress: 0, status: "idle" })));
    setIsUploading(true);
    try {
      return await Promise.allSettled(files.map((file) => performUpload(file, folder)));
    } finally {
      setIsUploading(false);
    }
  }, [performUpload]);

  const clearUploads = useCallback(() => setUploads([]), []);

  return {
    uploadFile,
    uploadFiles,
    uploadState: uploads.at(-1) || null,
    uploads,
    isUploading,
    clearUploads,
  };
}
